/**
 * Global axios authorization helper.
 *
 * Reads the admin session token from localStorage (key: "admin_token") and
 * installs it as the default Authorization header for every axios request.
 *
 * That way, every `axios.get/post/put/delete/patch` call made by any admin
 * component (BookingManager, Dashboard, PromotionsManager, PhotosManager,
 * ReviewsManager, AdminSettingsPage, etc.) automatically carries the bearer
 * token without needing a per-call change.
 *
 * Run `configureAdminAxios()` after login / OTP verification, after reading
 * the token back from storage on mount, and `clearAdminAxios()` on logout.
 */
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const STORAGE_KEY = "admin_token";

// ———————————————————————————————————————————————————————————
// Global request rewriter:
// Many legacy components build URLs as `${process.env.REACT_APP_BACKEND_URL}/api/...`
// at import time. In production (custom domain), that baked URL may point to
// the preview environment. This interceptor rewrites the URL host at request
// time to the correct same-origin backend derived from `window.location`.
// ———————————————————————————————————————————————————————————
const ENV_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

axios.interceptors.request.use((config) => {
  try {
    if (!config?.url) return config;
    const correctBase = getBackendUrl();
    if (ENV_URL && correctBase && correctBase !== ENV_URL && config.url.startsWith(ENV_URL)) {
      config.url = correctBase + config.url.slice(ENV_URL.length);
    }
  } catch {/* no-op */}
  return config;
});


export function getAdminToken() {
  try {
    return (
      localStorage.getItem(STORAGE_KEY) ||
      sessionStorage.getItem(STORAGE_KEY) ||
      null
    );
  } catch {
    return null;
  }
}

export function configureAdminAxios(token) {
  const t = token || getAdminToken();
  if (t) {
    axios.defaults.headers.common.Authorization = `Bearer ${t}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function clearAdminAxios() {
  delete axios.defaults.headers.common.Authorization;
}

// Auto-bootstrap at import time: if a token already exists in storage (e.g.
// page reload while logged in), install it immediately so the very first
// data fetch on mount (before the login effect runs) already succeeds.
configureAdminAxios();
